import type { GrowthBook } from '@growthbook/growthbook'

/**
 * Composable for managing feature flags using GrowthBook. Refer to https://zunocarbon.atlassian.net/wiki/spaces/DEV/pages/767721475/Guide+Growthbook+Feature+Flag
 */
export function useFeatureFlag() {
  const nuxtApp = useNuxtApp()
  const growthbook = nuxtApp.$growthbook as GrowthBook

  if (!growthbook) {
    throw new Error('GrowthBook is not initialized')
  }

  /**
   * Sets user attributes for feature flag targeting
   */
  async function setAttributes(attributes: Record<string, string>) {
    await growthbook.setAttributes(attributes)
  }

  /**
   * Retrieves current user attributes
   */
  const getAttributes = () => growthbook.getAttributes()

  /**
   * Checks if the translation widget feature is enabled
   */
  const isTranslationWidgetEnabled = () => growthbook.isOn('ff-enable-translation-widget')

  /**
   * Checks if the ESG dashboard widget feature is enabled.
   * @beta
   */
  const isEsgDashboardEnabled = () => growthbook.isOn('ff-enable-esg-dashboard-widget')

  /**
   * Gets the current maintenance message if any
   * If it is not null, the maintenance banner will be displayed on the dashboard
   */
  const maintenanceMessage = () => growthbook.getFeatureValue('maintenance-message', null)

  /**
   * Checks if the supplier portal feature is enabled
   * @beta
   */
  const isSupplierPortalEnabled = () => growthbook.isOn('ff-enable-supplier-portal')

  /**
   * Checks if the new revamped navbar feature is enabled
   * @beta
   */
  const isRevampedNavbarEnabled = () => growthbook.isOn('ff-enable-revamped-navbar')

  /**
   * Checks if ESG Revamped Menus are enabled
   * @beta
   */
  const isEsgRevampedEnabled = () => growthbook.isOn('ff-enable-esg-revamp')

  /**
   * Checks if ESG Submission Revamp is enabled
   * @beta
   */
  const isEsgSubmissionRevampEnabled = () => growthbook.isOn('ff-enable-esg-submission-revamp')

  /**
   * Checks if ESG Data Management Revamp is enabled
   * @beta
   */
  const isGhgAssignmentRevampEnabled = () => growthbook.isOn('ff-enable-ghg-assignment-revamp')

  /**
 * Checks if the Custom dashboard feature is enabled.
 * @beta
 */
  const isCustomDashboardEnabled = () => growthbook.isOn('ff-enable-custom-dashboard')

  /**
   * Gets the relevant PBI dashboard URI
   * If it is not null, the client's custom dashboard will be displayed
   */
  const customDashboardURI = () => growthbook.getFeatureValue('custom-dashboard-uri', null)

  /**
   * Checks if GHG Emission Factor Workflow is enabled
   * @beta
   */
  const isEmissionFactorWorkflowEnabled = () => growthbook.isOn('ff-improve-emission-factor-workflow')

  /**
   * Checks if GHG Entry Page Upgrade is enabled
   * @beta
   */
  const isGhgEntryPageUpgradeEnabled = () => growthbook.isOn('ff-upgrade-ghg-data-entry-page')

  /**
   * Checks if the new User Assignment is enabled
   * @beta
   */
  const isNewUserAssignmentEnabled = () => growthbook.isOn('ff-enable-new-user-assignment')

  /**
   * Checks if the view by user is enabled
   * @beta
   */
  const isViewByUserEnabled = () => growthbook.isOn('ff-enable-view-by-user')

  return {
    getAttributes,
    setAttributes,
    isTranslationWidgetEnabled,
    isEsgDashboardEnabled,
    maintenanceMessage,
    isSupplierPortalEnabled,
    isRevampedNavbarEnabled,
    isEsgRevampedEnabled,
    isEsgSubmissionRevampEnabled,
    isGhgAssignmentRevampEnabled,
    isCustomDashboardEnabled,
    customDashboardURI,
    isEmissionFactorWorkflowEnabled,
    isGhgEntryPageUpgradeEnabled,
    isNewUserAssignmentEnabled,
    isViewByUserEnabled,
  }
}
